import { ref, computed } from 'vue'
import { useFrontendStorage } from '../services/FrontendStorage'

const TOKEN_KEY = 'beads-auth-token'
const authStates = new Map()

/**
 * Decode JWT payload without verification (browser-side only).
 * Returns null if token is malformed.
 */
function decodePayload(jwt) {
  try {
    const parts = jwt.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1]))
    return payload
  } catch {
    return null
  }
}

function getAuthState(storage) {
  let state = authStates.get(storage)
  if (state) return state

  state = {
    token: ref(storage.getItem(TOKEN_KEY)),
  }
  authStates.set(storage, state)
  return state
}

export function resetAuthStateForTests() {
  authStates.clear()
}

export function useAuth() {
  const storage = useFrontendStorage()
  const state = getAuthState(storage)

  const isAuthenticated = computed(() => {
    if (!state.token.value) return false
    const payload = decodePayload(state.token.value)
    if (!payload || !payload.exp) return false
    return payload.exp > Date.now() / 1000
  })

  const tenantId = computed(() => {
    if (!state.token.value) return null
    const payload = decodePayload(state.token.value)
    return payload?.tenantId ?? null
  })

  const username = computed(() => {
    if (!state.token.value) return null
    const payload = decodePayload(state.token.value)
    return payload?.username ?? null
  })

  function setToken(newToken) {
    state.token.value = newToken
    storage.setItem(TOKEN_KEY, newToken)
  }

  function clearToken() {
    state.token.value = null
    storage.removeItem(TOKEN_KEY)
  }

  function getAuthHeaders() {
    if (!state.token.value) return {}
    const tid = tenantId.value
    const headers = { Authorization: `Bearer ${state.token.value}` }
    if (tid) headers['x-tenant-id'] = tid
    return headers
  }

  return { isAuthenticated, tenantId, username, token: state.token, setToken, clearToken, getAuthHeaders }
}
